import React, { useState, useEffect } from 'react';
import { X, Target, Calendar, DollarSign, Clock, Users, Building, FileText, CheckCircle, XCircle, TrendingUp, BarChart3, AlertTriangle, AlertCircle } from 'lucide-react';
import { useDashboard } from '../context/DashboardContext';

const ProjectDetailModal = ({ project, isOpen, onClose }) => {
  const { rocketlaneAPI } = useDashboard();
  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [taskAnalysis, setTaskAnalysis] = useState(null);
  const [activeTab, setActiveTab] = useState('risks');
  const [qualityFields, setQualityFields] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Fetch tasks when project changes
  useEffect(() => {
    if (isOpen && project?.projectId) {
      fetchTasks();
      fetchAdminUsers();
    }
  }, [isOpen, project?.projectId]);

  // Fetch admin users
  const fetchAdminUsers = async () => {
    setLoadingUsers(true);
    try {
      const users = await rocketlaneAPI.getUsers();
      const adminUsersList = users.filter(user => 
        user.role?.roleName === 'Admin' && user.status === 'ACTIVE'
      );
      setAdminUsers(adminUsersList);
    } catch (error) {
      console.error('Error fetching admin users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchTasks = async () => {
    if (!project?.projectId) {
      console.log('No project ID available for tasks fetch');
      return;
    }
    
    console.log(`Fetching tasks for project: ${project.projectId} (${project.projectName})`);
    console.log('RocketlaneAPI instance:', rocketlaneAPI);
    
    setLoadingTasks(true);
    try {
      const projectTasks = await rocketlaneAPI.getTasks(project.projectId);
      console.log(`Fetched ${projectTasks.length} tasks for project ${project.projectId}`);
      setTasks(projectTasks);
      analyzeTasks(projectTasks);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      console.error('Error details:', error.message);
    } finally {
      setLoadingTasks(false);
    }
  };

  const analyzeTasks = (projectTasks) => {
    console.log('Analyzing tasks:', projectTasks);
    if (!projectTasks || projectTasks.length === 0) {
      console.log('No tasks to analyze');
      setTaskAnalysis(null);
      return;
    }

    const today = new Date();
    const analysis = {
      totalTasks: 0,
      totalMilestones: 0,
      tasksInProgress: 0,
      tasksCompleted: 0,
      milestonesInProgress: 0,
      milestonesCompleted: 0,
      overdueTasks: 0,
      overdueMilestones: 0,
      tasksByStatus: {},
      milestonesByStatus: {},
      placeholderAnalysis: {}
    };

    projectTasks.forEach(task => {
      const isMilestone = task.type === 'MILESTONE';
      const isTask = task.type === 'TASK';
      
      if (isMilestone) {
        analysis.totalMilestones++;
        const status = task.status?.label || 'Unknown';
        analysis.milestonesByStatus[status] = (analysis.milestonesByStatus[status] || 0) + 1;
        
        if (status.toLowerCase().includes('completed')) {
          analysis.milestonesCompleted++;
        } else if (status.toLowerCase().includes('progress') || status.toLowerCase().includes('in progress')) {
          analysis.milestonesInProgress++;
        }

        // Check for overdue milestones
        if (task.dueDate && new Date(task.dueDate) < today && !status.toLowerCase().includes('completed')) {
          analysis.overdueMilestones++;
        }
      } else if (isTask) {
        analysis.totalTasks++;
        const status = task.status?.label || 'Unknown';
        analysis.tasksByStatus[status] = (analysis.tasksByStatus[status] || 0) + 1;
        
        if (status.toLowerCase().includes('completed')) {
          analysis.tasksCompleted++;
        } else if (status.toLowerCase().includes('progress') || status.toLowerCase().includes('in progress')) {
          analysis.tasksInProgress++;
        }

        // Check for overdue tasks
        if (task.dueDate && new Date(task.dueDate) < today && !status.toLowerCase().includes('completed')) {
          analysis.overdueTasks++;
        }

        // Analyze placeholders for unassigned tasks
        if (task.assignees?.placeholders && task.assignees.placeholders.length > 0) {
          task.assignees.placeholders.forEach(placeholder => {
            const placeholderName = placeholder.placeholderName;
            if (!analysis.placeholderAnalysis[placeholderName]) {
              analysis.placeholderAnalysis[placeholderName] = 0;
            }
            analysis.placeholderAnalysis[placeholderName]++;
          });
        }
      }
    });

    console.log('Task analysis completed:', analysis);
    setTaskAnalysis(analysis);
  };

  if (!isOpen || !project) return null;

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Not available';
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount, currency = 'USD') => {
    if (amount === null || amount === undefined) return 'Not available';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatYesNo = (value) => {
    return value === 1 ? 'Yes' : 'No';
  };

  const formatYesNoIcon = (value) => {
    return value === 1 ? (
      <CheckCircle size={20} className="status-icon-yes" />
    ) : (
      <XCircle size={20} className="status-icon-no" />
    );
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return '#10b981'; // Green
    if (percentage >= 60) return '#3b82f6'; // Blue
    if (percentage >= 40) return '#f59e0b'; // Yellow
    return '#ef4444'; // Red
  };

  const getHoursBarWidth = (hours, maxHours) => {
    if (!maxHours || maxHours === 0) return 0;
    return Math.min((hours / maxHours) * 100, 100);
  };

  const formatContractType = (contractType) => {
    if (!contractType) return 'Not specified';
    return contractType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getRiskLevel = (count, thresholds) => {
    if (count >= thresholds.high) return 'high';
    if (count >= thresholds.medium) return 'medium';
    return 'low';
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'high': return '#ef4444'; // Red
      case 'medium': return '#f59e0b'; // Orange
      case 'low': return '#10b981'; // Green
      default: return '#64748b'; // Gray
    }
  };

  const getRiskIcon = (level) => {
    switch (level) {
      case 'high': return <AlertTriangle size={16} />;
      case 'medium': return <AlertCircle size={16} />;
      case 'low': return <CheckCircle size={16} />;
      default: return <AlertCircle size={16} />;
    }
  };

  // Helper function to get quality field value
  const getQualityFieldValue = (fieldId) => {
    // First check if we have a local change
    if (qualityFields.hasOwnProperty(fieldId)) {
      return qualityFields[fieldId];
    }
    // Otherwise get from original project data
    const field = project.fields?.find(f => f.fieldId === fieldId);
    return field ? field.fieldValue : null;
  };

  // Helper function to get quality field label
  const getQualityFieldLabel = (fieldId) => {
    // First check if we have a local change
    if (qualityFields.hasOwnProperty(fieldId)) {
      return qualityFields[fieldId];
    }
    // Otherwise get from original project data
    const field = project.fields?.find(f => f.fieldId === fieldId);
    return field ? field.fieldValueLabel : '';
  };

  // Quality field options (from API calls)
  const qualityFieldOptions = {
    projectQuality: [
      { value: 1, label: 'Green - On Track', color: 'GREEN' },
      { value: 2, label: 'Yellow - Off Track', color: 'YELLOW' },
      { value: 3, label: 'Red - At Risk', color: 'RED' }
    ],
    overviewQuality: [
      { value: 1, label: 'Green', color: 'GREEN' },
      { value: 2, label: 'Yellow', color: 'YELLOW' },
      { value: 3, label: 'Red', color: 'RED' }
    ],
    projectPhasesQuality: [
      { value: 1, label: 'Green', color: 'GREEN' },
      { value: 2, label: 'Yellow', color: 'YELLOW' },
      { value: 3, label: 'Red', color: 'RED' }
    ],
    taskMilestoneQuality: [
      { value: 1, label: 'Green', color: 'GREEN' },
      { value: 2, label: 'Yellow', color: 'YELLOW' },
      { value: 3, label: 'Red', color: 'RED' }
    ],
    taskAssignmentQuality: [
      { value: 1, label: 'Green', color: 'GREEN' },
      { value: 2, label: 'Yellow', color: 'YELLOW' },
      { value: 3, label: 'Red', color: 'RED' }
    ],
    statusOfProjectQA: [
      { value: 1, label: 'Project quality review in progress', color: 'RED' },
      { value: 2, label: 'Follow - Up Required', color: 'YELLOW' },
      { value: 3, label: 'Complete', color: 'GREEN' }
    ]
  };

  // Handle quality field changes
  const handleQualityFieldChange = (fieldId, value) => {
    setQualityFields(prev => ({
      ...prev,
      [fieldId]: value
    }));
    setHasChanges(true);
  };

  // Save quality fields to RocketLane
  const handleSaveQualityFields = async () => {
    if (!hasChanges || !project?.projectId) return;

    setSaving(true);
    try {
      // Prepare fields data for API
      const fieldsToUpdate = [];
      
      // Add all quality fields that have been changed
      Object.entries(qualityFields).forEach(([fieldId, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          fieldsToUpdate.push({
            fieldId: parseInt(fieldId),
            fieldValue: value
          });
        }
      });

      if (fieldsToUpdate.length === 0) {
        alert('No changes to save');
        return;
      }

      const response = await fetch(
        `https://api.rocketlane.com/api/1.0/projects/${project.projectId}?includeFields=&includeAllFields=true`,
        {
          method: 'PUT',
          headers: {
            'accept': 'application/json',
            'api-key': 'rl-8c74e6d4-2fe6-4756-b7cd-8b650e5b27f5',
            'content-type': 'application/json'
          },
          body: JSON.stringify({
            fields: fieldsToUpdate
          })
        }
      );

      if (response.ok) {
        alert('Quality fields saved successfully!');
        setHasChanges(false);
        // Optionally refresh the project data
        // You might want to trigger a refresh of the project data here
      } else {
        const errorData = await response.json();
        alert(`Error saving quality fields: ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving quality fields:', error);
      alert('Error saving quality fields. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Tab configuration
  const tabs = [
    {
      id: 'risks',
      label: 'Risks',
      icon: AlertTriangle,
      color: '#ef4444'
    },
    {
      id: 'quality-audit',
      label: 'Quality Audit',
      icon: CheckCircle,
      color: '#10b981'
    },
    {
      id: 'budget-time',
      label: 'Budget & Time',
      icon: DollarSign,
      color: '#3b82f6'
    },
    {
      id: 'details',
      label: 'Project Details',
      icon: FileText,
      color: '#64748b'
    }
  ];

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'risks':
        return (
          <div className="tab-content">
            {/* Risk Analysis Section */}
            {loadingTasks && (
              <div className="risk-analysis-section">
                <h5>
                  <AlertTriangle size={16} />
                  Project Risk Analysis
                </h5>
                <div className="loading-tasks">
                  <div className="loading-spinner"></div>
                  <span>Loading project tasks...</span>
                </div>
              </div>
            )}
            
            {!loadingTasks && taskAnalysis && taskAnalysis.totalTasks + taskAnalysis.totalMilestones > 0 && (
              <div className="risk-analysis-section">
                <h5>
                  <AlertTriangle size={16} />
                  Project Risk Analysis
                </h5>
                
                {/* Enhanced Risk Score - Integrated into Risk Analysis */}
                <div className="enhanced-risk-score">
                  <h6>Enhanced Risk Score</h6>
                  <div className="risk-score-display">
                    {(() => {
                      // Calculate comprehensive risk score
                      const overdueTasks = taskAnalysis?.overdueTasks || 0;
                      const overdueMilestones = taskAnalysis?.overdueMilestones || 0;
                      const totalTasks = taskAnalysis?.totalTasks || 0;
                      const totalMilestones = taskAnalysis?.totalMilestones || 0;
                      
                      const budget = project.financials?.timeAndMaterialContract?.projectBudget || 0;
                      const spending = project.financials?.metrics?.actualSpending || project.financials?.metrics?.estimatedSpending || 0;
                      const budgetVariance = budget > 0 ? ((spending - budget) / budget) * 100 : 0;
                      
                      const budgetedHours = project.budgetedHours || 0;
                      const trackedHours = project.trackedHours || 0;
                      const timeVariance = budgetedHours > 0 ? ((trackedHours - budgetedHours) / budgetedHours) * 100 : 0;
                      
                      // Risk scoring algorithm
                      let riskScore = 0;
                      let riskFactors = [];
                      
                      // Task/Milestone risks (40% weight)
                      if (overdueTasks > 0) {
                        riskScore += Math.min(overdueTasks * 5, 20);
                        riskFactors.push(`${overdueTasks} overdue tasks`);
                      }
                      if (overdueMilestones > 0) {
                        riskScore += Math.min(overdueMilestones * 10, 20);
                        riskFactors.push(`${overdueMilestones} overdue milestones`);
                      }
                      
                      // Budget risks (30% weight)
                      if (budgetVariance > 10) {
                        riskScore += Math.min(budgetVariance - 10, 15);
                        riskFactors.push(`${budgetVariance.toFixed(1)}% budget overrun`);
                      }
                      
                      // Time risks (30% weight)
                      if (timeVariance > 20) {
                        riskScore += Math.min(timeVariance - 20, 15);
                        riskFactors.push(`${timeVariance.toFixed(1)}% time overrun`);
                      }
                      
                      // Normalize to 0-100 scale
                      riskScore = Math.min(riskScore, 100);
                      
                      const riskLevel = riskScore >= 70 ? 'high' : riskScore >= 40 ? 'medium' : 'low';
                      const riskColor = getRiskColor(riskLevel);
                      
                      return (
                        <div className="risk-score-content">
                          <div className="risk-score-main">
                            <div className="risk-score-value" style={{ color: riskColor }}>
                              {riskScore.toFixed(0)}/100
                            </div>
                            <div className="risk-score-level" style={{ color: riskColor }}>
                              {riskLevel.toUpperCase()}
                            </div>
                          </div>
                          <div className="risk-factors">
                            <span className="risk-factors-label">Risk Factors:</span>
                            <span className="risk-factors-list">
                              {riskFactors.length > 0 ? riskFactors.slice(0, 3).join(', ') : 'None identified'}
                            </span>
                          </div>
                          <div className="risk-breakdown">
                            <div className="risk-breakdown-item">
                              <span className="risk-breakdown-label">Budget Risk:</span>
                              <span className={`risk-breakdown-value ${budgetVariance <= 10 ? 'positive' : 'negative'}`}>
                                {budgetVariance.toFixed(1)}%
                              </span>
                            </div>
                            <div className="risk-breakdown-item">
                              <span className="risk-breakdown-label">Time Risk:</span>
                              <span className={`risk-breakdown-value ${timeVariance <= 20 ? 'positive' : 'negative'}`}>
                                {timeVariance.toFixed(1)}%
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
                
                {/* Task and Milestone Overview */}
                <div className="task-overview-grid">
                  <div className="task-overview-card">
                    <h6>Tasks Overview</h6>
                    <div className="task-stats">
                      <div className="task-stat">
                        <span className="stat-label">Total</span>
                        <span className="stat-value">{taskAnalysis.totalTasks}</span>
                      </div>
                      <div className="task-stat">
                        <span className="stat-label">In Progress</span>
                        <span className="stat-value in-progress">{taskAnalysis.tasksInProgress}</span>
                      </div>
                      <div className="task-stat">
                        <span className="stat-label">Completed</span>
                        <span className="stat-value completed">{taskAnalysis.tasksCompleted}</span>
                      </div>
                    </div>
                    
                    {/* Task Progress Chart */}
                    {taskAnalysis.totalTasks > 0 && (
                      <div className="mini-progress-chart">
                        <div className="progress-bar-mini">
                          <div 
                            className="progress-fill-mini completed"
                            style={{ width: `${(taskAnalysis.tasksCompleted / taskAnalysis.totalTasks) * 100}%` }}
                          ></div>
                          <div 
                            className="progress-fill-mini in-progress"
                            style={{ width: `${(taskAnalysis.tasksInProgress / taskAnalysis.totalTasks) * 100}%` }}
                          ></div>
                        </div>
                        <div className="progress-labels">
                          <span>Completed: {taskAnalysis.tasksCompleted}</span>
                          <span>In Progress: {taskAnalysis.tasksInProgress}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="task-overview-card">
                    <h6>Milestones Overview</h6>
                    <div className="task-stats">
                      <div className="task-stat">
                        <span className="stat-label">Total</span>
                        <span className="stat-value">{taskAnalysis.totalMilestones}</span>
                      </div>
                      <div className="task-stat">
                        <span className="stat-label">In Progress</span>
                        <span className="stat-value in-progress">{taskAnalysis.milestonesInProgress}</span>
                      </div>
                      <div className="task-stat">
                        <span className="stat-label">Completed</span>
                        <span className="stat-value completed">{taskAnalysis.milestonesCompleted}</span>
                      </div>
                    </div>
                    
                    {/* Milestone Progress Chart */}
                    {taskAnalysis.totalMilestones > 0 && (
                      <div className="mini-progress-chart">
                        <div className="progress-bar-mini">
                          <div 
                            className="progress-fill-mini completed"
                            style={{ width: `${(taskAnalysis.milestonesCompleted / taskAnalysis.totalMilestones) * 100}%` }}
                          ></div>
                          <div 
                            className="progress-fill-mini in-progress"
                            style={{ width: `${(taskAnalysis.milestonesInProgress / taskAnalysis.totalMilestones) * 100}%` }}
                          ></div>
                        </div>
                        <div className="progress-labels">
                          <span>Completed: {taskAnalysis.milestonesCompleted}</span>
                          <span>In Progress: {taskAnalysis.milestonesInProgress}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Risk Indicators */}
                <div className="risk-indicators">
                  <h6>Risk Indicators</h6>
                  <div className="risk-grid">
                    {(() => {
                      const taskRiskLevel = getRiskLevel(taskAnalysis.overdueTasks, { medium: 5, high: 10 });
                      const milestoneRiskLevel = getRiskLevel(taskAnalysis.overdueMilestones, { medium: 1, high: 2 });
                      
                      return (
                        <>
                          <div className="risk-item" style={{ color: getRiskColor(taskRiskLevel) }}>
                            <div className="risk-icon">
                              {getRiskIcon(taskRiskLevel)}
                            </div>
                            <div className="risk-info">
                              <span className="risk-count">{taskAnalysis.overdueTasks}</span>
                              <span className="risk-label">Overdue Tasks</span>
                            </div>
                          </div>
                          
                          <div className="risk-item" style={{ color: getRiskColor(milestoneRiskLevel) }}>
                            <div className="risk-icon">
                              {getRiskIcon(milestoneRiskLevel)}
                            </div>
                            <div className="risk-info">
                              <span className="risk-count">{taskAnalysis.overdueMilestones}</span>
                              <span className="risk-label">Overdue Milestones</span>
                            </div>
                          </div>
                          
                          {/* Additional Risk Indicators */}
                          {taskAnalysis.totalTasks > 0 && (
                            <div className="risk-item">
                              <div className="risk-icon">
                                <Clock size={16} />
                              </div>
                              <div className="risk-info">
                                <span className="risk-count">{Math.round((taskAnalysis.tasksInProgress / taskAnalysis.totalTasks) * 100)}%</span>
                                <span className="risk-label">Tasks In Progress</span>
                              </div>
                            </div>
                          )}
                          
                          {taskAnalysis.totalMilestones > 0 && (
                            <div className="risk-item">
                              <div className="risk-icon">
                                <Target size={16} />
                              </div>
                              <div className="risk-info">
                                <span className="risk-count">{Math.round((taskAnalysis.milestonesInProgress / taskAnalysis.totalMilestones) * 100)}%</span>
                                <span className="risk-label">Milestones In Progress</span>
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                                 </div>
               </div>
             )}

                           {!loadingTasks && taskAnalysis && taskAnalysis.totalTasks + taskAnalysis.totalMilestones === 0 && (
                <div className="risk-analysis-section">
                  <h5>
                    <AlertTriangle size={16} />
                    Project Risk Analysis
                  </h5>
                  <div className="no-tasks-message">
                    <span>No tasks or milestones found for this project.</span>
                  </div>
                </div>
              )}

              {/* OneAI Risk Predictions Section */}
              <div className="oneai-risk-predictions-section">
                <div className="oneai-risk-header">
                  <h5>
                    <AlertTriangle size={16} />
                    OneAI Risk Predictions
                  </h5>
                  <span className="ai-badge">AI-Powered Analysis</span>
                </div>
                
                <div className="risk-predictions-grid">
                  {/* Resource Allocation Risks */}
                  <div className="prediction-card">
                    <div className="prediction-header">
                      <Users size={16} />
                      <h6>Resource Allocation Risks</h6>
                    </div>
                    <div className="prediction-content">
                      {(() => {
                        const teamSize = project.teamMembers?.members?.length || 0;
                        const totalTasks = taskAnalysis?.totalTasks || 0;
                        const tasksPerMember = teamSize > 0 ? totalTasks / teamSize : 0;
                        const budgetedHours = project.budgetedHours || 0;
                        const trackedHours = project.trackedHours || 0;
                        const hoursUtilization = budgetedHours > 0 ? (trackedHours / budgetedHours) * 100 : 0;
                        
                        let riskLevel = 'low';
                        let riskFactors = [];
                        
                        if (tasksPerMember > 15) {
                          riskLevel = 'high';
                          riskFactors.push(`High workload: ${tasksPerMember.toFixed(1)} tasks per team member`);
                        } else if (tasksPerMember > 10) {
                          riskLevel = 'medium';
                          riskFactors.push(`Moderate workload: ${tasksPerMember.toFixed(1)} tasks per team member`);
                        }
                        
                        if (hoursUtilization > 120) {
                          riskLevel = riskLevel === 'high' ? 'high' : 'medium';
                          riskFactors.push(`Over-utilization: ${hoursUtilization.toFixed(1)}% of budgeted hours used`);
                        } else if (hoursUtilization < 60) {
                          riskFactors.push(`Under-utilization: ${hoursUtilization.toFixed(1)}% of budgeted hours used`);
                        }
                        
                        if (teamSize === 1 && totalTasks > 5) {
                          riskLevel = 'high';
                          riskFactors.push('Single team member handling multiple tasks');
                        }
                        
                        return (
                          <>
                            <div className={`risk-indicator ${riskLevel}`}>
                              <span className="risk-level">{riskLevel.toUpperCase()} RISK</span>
                            </div>
                            <div className="risk-factors-list">
                              {riskFactors.length > 0 ? (
                                riskFactors.map((factor, index) => (
                                  <div key={index} className="risk-factor">
                                    <AlertCircle size={12} />
                                    <span>{factor}</span>
                                  </div>
                                ))
                              ) : (
                                <div className="risk-factor positive">
                                  <CheckCircle size={12} />
                                  <span>Resource allocation appears balanced</span>
                                </div>
                              )}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Timeline Compression Risks */}
                  <div className="prediction-card">
                    <div className="prediction-header">
                      <Calendar size={16} />
                      <h6>Timeline Compression Risks</h6>
                    </div>
                    <div className="prediction-content">
                      {(() => {
                        const startDate = new Date(project.startDate);
                        const dueDate = new Date(project.dueDate);
                        const today = new Date();
                        const totalDuration = (dueDate - startDate) / (1000 * 60 * 60 * 24);
                        const elapsedDays = (today - startDate) / (1000 * 60 * 60 * 24);
                        const remainingDays = (dueDate - today) / (1000 * 60 * 60 * 24);
                        const progressPercentage = project.progressPercentage || 0;
                        const expectedProgress = elapsedDays / totalDuration * 100;
                        const progressGap = expectedProgress - progressPercentage;
                        
                        let riskLevel = 'low';
                        let riskFactors = [];
                        
                        if (remainingDays < 30 && progressPercentage < 80) {
                          riskLevel = 'high';
                          riskFactors.push(`Critical timeline: ${remainingDays.toFixed(0)} days remaining`);
                        } else if (remainingDays < 60 && progressPercentage < 60) {
                          riskLevel = 'medium';
                          riskFactors.push(`Tight timeline: ${remainingDays.toFixed(0)} days remaining`);
                        }
                        
                        if (progressGap > 20) {
                          riskLevel = riskLevel === 'high' ? 'high' : 'medium';
                          riskFactors.push(`Behind schedule: ${progressGap.toFixed(1)}% behind expected progress`);
                        }
                        
                        if (totalDuration < 30) {
                          riskFactors.push(`Short project duration: ${totalDuration.toFixed(0)} days total`);
                        }
                        
                        return (
                          <>
                            <div className={`risk-indicator ${riskLevel}`}>
                              <span className="risk-level">{riskLevel.toUpperCase()} RISK</span>
                            </div>
                            <div className="risk-factors-list">
                              {riskFactors.length > 0 ? (
                                riskFactors.map((factor, index) => (
                                  <div key={index} className="risk-factor">
                                    <AlertCircle size={12} />
                                    <span>{factor}</span>
                                  </div>
                                ))
                              ) : (
                                <div className="risk-factor positive">
                                  <CheckCircle size={12} />
                                  <span>Timeline appears manageable</span>
                                </div>
                              )}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Stakeholder Engagement Risks */}
                  <div className="prediction-card">
                    <div className="prediction-header">
                      <Building size={16} />
                      <h6>Stakeholder Engagement Risks</h6>
                    </div>
                    <div className="prediction-content">
                      {(() => {
                        const customersInvited = project.customersInvited || 0;
                        const customersJoined = project.customersJoined || 0;
                        const customerEngagement = customersInvited > 0 ? (customersJoined / customersInvited) * 100 : 0;
                        const teamMembers = project.teamMembers?.members?.length || 0;
                        const customerMembers = project.teamMembers?.customers?.length || 0;
                        
                        let riskLevel = 'low';
                        let riskFactors = [];
                        
                        if (customerEngagement < 50) {
                          riskLevel = 'high';
                          riskFactors.push(`Low customer engagement: ${customerEngagement.toFixed(0)}% of invited customers joined`);
                        } else if (customerEngagement < 75) {
                          riskLevel = 'medium';
                          riskFactors.push(`Moderate customer engagement: ${customerEngagement.toFixed(0)}% of invited customers joined`);
                        }
                        
                        if (customersInvited === 0) {
                          riskLevel = 'medium';
                          riskFactors.push('No customer stakeholders invited');
                        }
                        
                        if (customerMembers === 0 && customersJoined > 0) {
                          riskFactors.push('Customers joined but not assigned to team');
                        }
                        
                        return (
                          <>
                            <div className={`risk-indicator ${riskLevel}`}>
                              <span className="risk-level">{riskLevel.toUpperCase()} RISK</span>
                            </div>
                            <div className="risk-factors-list">
                              {riskFactors.length > 0 ? (
                                riskFactors.map((factor, index) => (
                                  <div key={index} className="risk-factor">
                                    <AlertCircle size={12} />
                                    <span>{factor}</span>
                                  </div>
                                ))
                              ) : (
                                <div className="risk-factor positive">
                                  <CheckCircle size={12} />
                                  <span>Strong stakeholder engagement</span>
                                </div>
                              )}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Financial Health Risks */}
                  <div className="prediction-card">
                    <div className="prediction-header">
                      <DollarSign size={16} />
                      <h6>Financial Health Risks</h6>
                    </div>
                    <div className="prediction-content">
                      {(() => {
                        const budget = project.financials?.timeAndMaterialContract?.projectBudget || 0;
                        const actualRevenue = project.financials?.metrics?.actualRevenue || 0;
                        const estimatedRevenue = project.financials?.metrics?.estimatedRevenue || 0;
                        const actualSpending = project.financials?.metrics?.actualSpending || 0;
                        const estimatedSpending = project.financials?.metrics?.estimatedSpending || 0;
                        
                        const revenue = actualRevenue || estimatedRevenue;
                        const spending = actualSpending || estimatedSpending;
                        const profitMargin = revenue > 0 ? ((revenue - spending) / revenue) * 100 : 0;
                        const budgetVariance = budget > 0 ? ((spending - budget) / budget) * 100 : 0;
                        
                        let riskLevel = 'low';
                        let riskFactors = [];
                        
                        if (profitMargin < -10) {
                          riskLevel = 'high';
                          riskFactors.push(`Negative profit margin: ${profitMargin.toFixed(1)}%`);
                        } else if (profitMargin < 5) {
                          riskLevel = 'medium';
                          riskFactors.push(`Low profit margin: ${profitMargin.toFixed(1)}%`);
                        }
                        
                        if (budgetVariance > 20) {
                          riskLevel = riskLevel === 'high' ? 'high' : 'medium';
                          riskFactors.push(`Budget overrun: ${budgetVariance.toFixed(1)}% over budget`);
                        }
                        
                        if (budget === 0) {
                          riskFactors.push('No project budget defined');
                        }
                        
                        if (revenue === 0 && spending > 0) {
                          riskFactors.push('Spending without revenue recognition');
                        }
                        
                        return (
                          <>
                            <div className={`risk-indicator ${riskLevel}`}>
                              <span className="risk-level">{riskLevel.toUpperCase()} RISK</span>
                            </div>
                            <div className="risk-factors-list">
                              {riskFactors.length > 0 ? (
                                riskFactors.map((factor, index) => (
                                  <div key={index} className="risk-factor">
                                    <AlertCircle size={12} />
                                    <span>{factor}</span>
                                  </div>
                                ))
                              ) : (
                                <div className="risk-factor positive">
                                  <CheckCircle size={12} />
                                  <span>Financial health appears stable</span>
                                </div>
                              )}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Scope Creep Risks */}
                  <div className="prediction-card">
                    <div className="prediction-header">
                      <Target size={16} />
                      <h6>Scope Creep Risks</h6>
                    </div>
                    <div className="prediction-content">
                      {(() => {
                        const totalTasks = taskAnalysis?.totalTasks || 0;
                        const totalMilestones = taskAnalysis?.totalMilestones || 0;
                        const overdueTasks = taskAnalysis?.overdueTasks || 0;
                        const overdueMilestones = taskAnalysis?.overdueMilestones || 0;
                        const projectAge = project.projectAgeInDays || 0;
                        const plannedDuration = project.plannedDurationInDays || 0;
                        
                        const taskComplexity = totalTasks / Math.max(totalMilestones, 1);
                        const overdueRatio = (overdueTasks + overdueMilestones) / Math.max(totalTasks + totalMilestones, 1);
                        const timelinePressure = projectAge / Math.max(plannedDuration, 1);
                        
                        let riskLevel = 'low';
                        let riskFactors = [];
                        
                        if (taskComplexity > 8) {
                          riskLevel = 'medium';
                          riskFactors.push(`High task complexity: ${taskComplexity.toFixed(1)} tasks per milestone`);
                        }
                        
                        if (overdueRatio > 0.3) {
                          riskLevel = riskLevel === 'high' ? 'high' : 'medium';
                          riskFactors.push(`High overdue ratio: ${(overdueRatio * 100).toFixed(0)}% of items overdue`);
                        }
                        
                        if (timelinePressure > 0.8 && project.progressPercentage < 70) {
                          riskLevel = 'high';
                          riskFactors.push(`Timeline pressure: ${(timelinePressure * 100).toFixed(0)}% of time elapsed`);
                        }
                        
                        if (totalTasks > 50) {
                          riskFactors.push(`Large scope: ${totalTasks} total tasks`);
                        }
                        
                        return (
                          <>
                            <div className={`risk-indicator ${riskLevel}`}>
                              <span className="risk-level">{riskLevel.toUpperCase()} RISK</span>
                            </div>
                            <div className="risk-factors-list">
                              {riskFactors.length > 0 ? (
                                riskFactors.map((factor, index) => (
                                  <div key={index} className="risk-factor">
                                    <AlertCircle size={12} />
                                    <span>{factor}</span>
                                  </div>
                                ))
                              ) : (
                                <div className="risk-factor positive">
                                  <CheckCircle size={12} />
                                  <span>Scope appears well-controlled</span>
                                </div>
                              )}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Communication & Coordination Risks */}
                  <div className="prediction-card">
                    <div className="prediction-header">
                      <Users size={16} />
                      <h6>Communication & Coordination Risks</h6>
                    </div>
                    <div className="prediction-content">
                      {(() => {
                        const teamMembers = project.teamMembers?.members?.length || 0;
                        const customerMembers = project.teamMembers?.customers?.length || 0;
                        const totalStakeholders = teamMembers + customerMembers;
                        const hasOwner = project.owner ? 1 : 0;
                        const lastUpdated = project.updatedAt ? new Date(project.updatedAt) : null;
                        const daysSinceUpdate = lastUpdated ? (new Date() - lastUpdated) / (1000 * 60 * 60 * 24) : 0;
                        
                        let riskLevel = 'low';
                        let riskFactors = [];
                        
                        if (totalStakeholders > 10) {
                          riskLevel = 'medium';
                          riskFactors.push(`Large stakeholder group: ${totalStakeholders} total members`);
                        }
                        
                        if (hasOwner === 0) {
                          riskLevel = 'high';
                          riskFactors.push('No project owner assigned');
                        }
                        
                        if (daysSinceUpdate > 7) {
                          riskLevel = riskLevel === 'high' ? 'high' : 'medium';
                          riskFactors.push(`Stale project data: ${daysSinceUpdate.toFixed(0)} days since last update`);
                        }
                        
                        if (teamMembers === 0) {
                          riskLevel = 'high';
                          riskFactors.push('No team members assigned');
                        }
                        
                        if (customerMembers === 0 && project.customer) {
                          riskFactors.push('Customer not added to team');
                        }
                        
                        return (
                          <>
                            <div className={`risk-indicator ${riskLevel}`}>
                              <span className="risk-level">{riskLevel.toUpperCase()} RISK</span>
                            </div>
                            <div className="risk-factors-list">
                              {riskFactors.length > 0 ? (
                                riskFactors.map((factor, index) => (
                                  <div key={index} className="risk-factor">
                                    <AlertCircle size={12} />
                                    <span>{factor}</span>
                                  </div>
                                ))
                              ) : (
                                <div className="risk-factor positive">
                                  <CheckCircle size={12} />
                                  <span>Communication structure appears solid</span>
                                </div>
                              )}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
          </div>
        );

      case 'quality-audit':
        return (
          <div className="tab-content">
            {/* Project Quality Audit Section */}
            <div className="quality-audit-section">
              <div className="quality-audit-header">
                <h5>
                  <CheckCircle size={16} />
                  Project Quality Audit
                </h5>
                <button
                  onClick={handleSaveQualityFields}
                  disabled={saving || !hasChanges}
                  className="save-quality-button"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
              <div className="quality-audit-grid">
                {/* Row 1: Project Quality & Project Quality Reason */}
                <div className="quality-field">
                  <label className="quality-label">Project Quality</label>
                  <select
                    value={getQualityFieldValue(2004934) || ''}
                    onChange={(e) => handleQualityFieldChange(2004934, parseInt(e.target.value))}
                    className="quality-select"
                  >
                    <option value="">Select Quality</option>
                    {qualityFieldOptions.projectQuality.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="quality-field">
                  <label className="quality-label">Project Quality Reason</label>
                  <textarea
                    value={getQualityFieldLabel(2005045) || ''}
                    onChange={(e) => handleQualityFieldChange(2005045, e.target.value)}
                    className="quality-textarea"
                    placeholder="Enter quality reason..."
                    rows="2"
                  />
                </div>

                {/* Row 2: Overview Quality & Overview Quality Reason */}
                <div className="quality-field">
                  <label className="quality-label">Overview Quality</label>
                  <select
                    value={getQualityFieldValue(2005250) || ''}
                    onChange={(e) => handleQualityFieldChange(2005250, parseInt(e.target.value))}
                    className="quality-select"
                  >
                    <option value="">Select Quality</option>
                    {qualityFieldOptions.overviewQuality.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="quality-field">
                  <label className="quality-label">Overview Quality Reason</label>
                  <textarea
                    value={getQualityFieldLabel(2005251) || ''}
                    onChange={(e) => handleQualityFieldChange(2005251, e.target.value)}
                    className="quality-textarea"
                    placeholder="Enter overview quality reason..."
                    rows="2"
                  />
                </div>

                {/* Row 3: Project Phases Quality & Project Phase Reason */}
                <div className="quality-field">
                  <label className="quality-label">Project Phases Quality</label>
                  <select
                    value={getQualityFieldValue(2005253) || ''}
                    onChange={(e) => handleQualityFieldChange(2005253, parseInt(e.target.value))}
                    className="quality-select"
                  >
                    <option value="">Select Quality</option>
                    {qualityFieldOptions.projectPhasesQuality.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="quality-field">
                  <label className="quality-label">Project Phase Reason</label>
                  <textarea
                    value={getQualityFieldLabel(2005254) || ''}
                    onChange={(e) => handleQualityFieldChange(2005254, e.target.value)}
                    className="quality-textarea"
                    placeholder="Enter phase reason..."
                    rows="2"
                  />
                </div>

                {/* Row 4: Task & Milestone Quality & Task & Milestone Reason */}
                <div className="quality-field">
                  <label className="quality-label">Task & Milestone Quality</label>
                  <select
                    value={getQualityFieldValue(2005046) || ''}
                    onChange={(e) => handleQualityFieldChange(2005046, parseInt(e.target.value))}
                    className="quality-select"
                  >
                    <option value="">Select Quality</option>
                    {qualityFieldOptions.taskMilestoneQuality.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="quality-field">
                  <label className="quality-label">Task & Milestone Reason</label>
                  <textarea
                    value={getQualityFieldLabel(2005255) || ''}
                    onChange={(e) => handleQualityFieldChange(2005255, e.target.value)}
                    className="quality-textarea"
                    placeholder="Enter task & milestone reason..."
                    rows="2"
                  />
                </div>

                {/* Row 5: Task Assignment Quality & Task Assignment Reason */}
                <div className="quality-field">
                  <label className="quality-label">Task Assignment Quality</label>
                  <select
                    value={getQualityFieldValue(2005256) || ''}
                    onChange={(e) => handleQualityFieldChange(2005256, parseInt(e.target.value))}
                    className="quality-select"
                  >
                    <option value="">Select Quality</option>
                    {qualityFieldOptions.taskAssignmentQuality.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  
                  {/* Placeholder Analysis */}
                  {taskAnalysis?.placeholderAnalysis && Object.keys(taskAnalysis.placeholderAnalysis).length > 0 && (
                    <div className="placeholder-suggestions">
                      <span className="placeholder-label">Unassigned Task Suggestions:</span>
                      <div className="placeholder-list">
                        {Object.entries(taskAnalysis.placeholderAnalysis).map(([placeholderName, count]) => (
                          <div key={placeholderName} className="placeholder-item">
                            <span className="placeholder-count">{count}</span>
                            <span className="placeholder-text">tasks with "{placeholderName}" placeholders</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="quality-field">
                  <label className="quality-label">Task Assignment Reason</label>
                  <textarea
                    value={getQualityFieldLabel(2005258) || ''}
                    onChange={(e) => handleQualityFieldChange(2005258, e.target.value)}
                    className="quality-textarea"
                    placeholder="Enter task assignment reason..."
                    rows="2"
                  />
                </div>

                {/* Row 6: CRS Following Tasks & Quality Review Date */}
                <div className="quality-field">
                  <label className="quality-label">CRS Following Tasks?</label>
                  <div className="quality-input">
                    <input
                      type="checkbox"
                      checked={getQualityFieldValue(1783104) || false}
                      onChange={(e) => handleQualityFieldChange(1783104, e.target.checked)}
                    />
                    <span>Yes</span>
                  </div>
                </div>

                <div className="quality-field">
                  <label className="quality-label">Quality Review Date</label>
                  <input
                    type="date"
                    value={getQualityFieldLabel(2004935) || ''}
                    onChange={(e) => handleQualityFieldChange(2004935, e.target.value)}
                    className="quality-input"
                  />
                </div>

                {/* Row 7: Reviewed by & Status of Project QA */}
                <div className="quality-field">
                  <label className="quality-label">Reviewed by</label>
                  <select
                    value={getQualityFieldValue(2005306) || ''}
                    onChange={(e) => handleQualityFieldChange(2005306, parseInt(e.target.value))}
                    className="quality-select"
                    disabled={loadingUsers}
                  >
                    <option value="">Select Reviewer</option>
                    {adminUsers.map(user => (
                      <option key={user.userId} value={user.userId}>
                        {user.firstName} {user.lastName} ({user.email})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="quality-field">
                  <label className="quality-label">Status of Project QA</label>
                  <select
                    value={getQualityFieldValue(2005776) || ''}
                    onChange={(e) => handleQualityFieldChange(2005776, parseInt(e.target.value))}
                    className="quality-select"
                  >
                    <option value="">Select Status</option>
                    {qualityFieldOptions.statusOfProjectQA.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        );

      case 'budget-time':
        return (
          <div className="tab-content">
            {/* Enhanced Financial & Risk Metrics Section */}
            <div className="enhanced-metrics-section">
              <h5>
                <DollarSign size={16} />
                Financial & Risk Analysis
              </h5>
              
              <div className="enhanced-metrics-grid">
                {/* Project Profitability */}
                <div className="metric-card">
                  <h6>Project Profitability</h6>
                  <div className="metric-content">
                    {(() => {
                      const budget = project.financials?.timeAndMaterialContract?.projectBudget || 0;
                      const actualRevenue = project.financials?.metrics?.actualRevenue || 0;
                      const estimatedRevenue = project.financials?.metrics?.estimatedRevenue || 0;
                      const trackedHours = project.trackedHours || 0;
                      const billableHours = project.billableHours || 0;
                      
                      // Calculate profitability metrics
                      const revenue = actualRevenue || estimatedRevenue;
                      const costPerHour = trackedHours > 0 ? (budget / trackedHours) : 0;
                      const profit = revenue - budget;
                      const profitMargin = revenue > 0 ? (profit / revenue) * 100 : 0;
                      
                      return (
                        <>
                          <div className="metric-row">
                            <span className="metric-label">Budget</span>
                            <span className="metric-value">{formatCurrency(budget)}</span>
                          </div>
                          <div className="metric-row">
                            <span className="metric-label">Revenue</span>
                            <span className="metric-value">{formatCurrency(revenue)}</span>
                          </div>
                          <div className="metric-row">
                            <span className="metric-label">Profit</span>
                            <span className={`metric-value ${profit >= 0 ? 'positive' : 'negative'}`}>
                              {formatCurrency(profit)}
                            </span>
                          </div>
                          <div className="metric-row">
                            <span className="metric-label">Profit Margin</span>
                            <span className={`metric-value ${profitMargin >= 0 ? 'positive' : 'negative'}`}>
                              {profitMargin.toFixed(1)}%
                            </span>
                          </div>
                          <div className="metric-row">
                            <span className="metric-label">Cost per Hour</span>
                            <span className="metric-value">{formatCurrency(costPerHour)}</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Efficiency Gap Analysis */}
                <div className="metric-card">
                  <h6>Efficiency Gap Analysis</h6>
                  <div className="metric-content">
                    {(() => {
                      const budgetedHours = project.budgetedHours || 0;
                      const trackedHours = project.trackedHours || 0;
                      const billableHours = project.billableHours || 0;
                      
                      // Calculate efficiency metrics
                      const efficiencyGap = budgetedHours > 0 ? ((trackedHours - budgetedHours) / budgetedHours) * 100 : 0;
                      const billableEfficiency = trackedHours > 0 ? (billableHours / trackedHours) * 100 : 0;
                      const utilizationRate = budgetedHours > 0 ? (trackedHours / budgetedHours) * 100 : 0;
                      
                      return (
                        <>
                          <div className="metric-row">
                            <span className="metric-label">Budgeted Hours</span>
                            <span className="metric-value">{budgetedHours.toFixed(1)}h</span>
                          </div>
                          <div className="metric-row">
                            <span className="metric-label">Tracked Hours</span>
                            <span className="metric-value">{trackedHours.toFixed(1)}h</span>
                          </div>
                          <div className="metric-row">
                            <span className="metric-label">Efficiency Gap</span>
                            <span className={`metric-value ${efficiencyGap <= 0 ? 'positive' : 'negative'}`}>
                              {efficiencyGap.toFixed(1)}%
                            </span>
                          </div>
                          <div className="metric-row">
                            <span className="metric-label">Billable Efficiency</span>
                            <span className="metric-value">{billableEfficiency.toFixed(1)}%</span>
                          </div>
                          <div className="metric-row">
                            <span className="metric-label">Utilization Rate</span>
                            <span className="metric-value">{utilizationRate.toFixed(1)}%</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Budget vs Actual Spending */}
                <div className="metric-card">
                  <h6>Budget vs Actual Spending</h6>
                  <div className="metric-content">
                    {(() => {
                      const budget = project.financials?.timeAndMaterialContract?.projectBudget || 0;
                      const actualSpending = project.financials?.metrics?.actualSpending || 0;
                      const estimatedSpending = project.financials?.metrics?.estimatedSpending || 0;
                      const budgetedHours = project.budgetedHours || 0;
                      const trackedHours = project.trackedHours || 0;
                      
                      // Calculate spending metrics
                      const spending = actualSpending || estimatedSpending;
                      const budgetVariance = budget > 0 ? ((spending - budget) / budget) * 100 : 0;
                      const hourlyRate = budgetedHours > 0 ? (budget / budgetedHours) : 0;
                      const actualHourlyCost = trackedHours > 0 ? (spending / trackedHours) : 0;
                      
                      return (
                        <>
                          <div className="metric-row">
                            <span className="metric-label">Budget</span>
                            <span className="metric-value">{formatCurrency(budget)}</span>
                          </div>
                          <div className="metric-row">
                            <span className="metric-label">Actual Spending</span>
                            <span className="metric-value">{formatCurrency(spending)}</span>
                          </div>
                          <div className="metric-row">
                            <span className="metric-label">Budget Variance</span>
                            <span className={`metric-value ${budgetVariance <= 0 ? 'positive' : 'negative'}`}>
                              {budgetVariance.toFixed(1)}%
                            </span>
                          </div>
                          <div className="metric-row">
                            <span className="metric-label">Budgeted Hourly Rate</span>
                            <span className="metric-value">{formatCurrency(hourlyRate)}</span>
                          </div>
                          <div className="metric-row">
                            <span className="metric-label">Actual Hourly Cost</span>
                            <span className="metric-value">{formatCurrency(actualHourlyCost)}</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>

            {/* Hours and Financial Overview - Side by Side */}
            <div className="hours-financial-grid">
              {/* Hours Breakdown with Bar Charts */}
              <div className="hours-breakdown-section">
                <h5>
                  <Clock size={16} />
                  Hours Breakdown
                </h5>
                <div className="hours-chart">
                  {(() => {
                    const maxHours = Math.max(
                      project.budgetedHours || 0,
                      project.trackedHours || 0,
                      project.billableHours || 0,
                      project.remainingHours || 0,
                      100 // Minimum scale
                    );
                    
                    return (
                      <div className="hours-bars">
                        <div className="hour-bar-item">
                          <div className="hour-bar-label">
                            <span>Budgeted</span>
                            <span className="hour-value">{project.budgetedHours?.toFixed(1) || 0}h</span>
                          </div>
                          <div className="hour-bar-container">
                            <div 
                              className="hour-bar budgeted"
                              style={{ width: `${getHoursBarWidth(project.budgetedHours || 0, maxHours)}%` }}
                            ></div>
                          </div>
                        </div>
                        
                        <div className="hour-bar-item">
                          <div className="hour-bar-label">
                            <span>Tracked</span>
                            <span className="hour-value">{project.trackedHours?.toFixed(1) || 0}h</span>
                          </div>
                          <div className="hour-bar-container">
                            <div 
                              className="hour-bar tracked"
                              style={{ width: `${getHoursBarWidth(project.trackedHours || 0, maxHours)}%` }}
                            ></div>
                          </div>
                        </div>
                        
                        <div className="hour-bar-item">
                          <div className="hour-bar-label">
                            <span>Billable</span>
                            <span className="hour-value">{project.billableHours?.toFixed(1) || 0}h</span>
                          </div>
                          <div className="hour-bar-container">
                            <div 
                              className="hour-bar billable"
                              style={{ width: `${getHoursBarWidth(project.billableHours || 0, maxHours)}%` }}
                            ></div>
                          </div>
                        </div>
                        
                        <div className="hour-bar-item">
                          <div className="hour-bar-label">
                            <span>Remaining</span>
                            <span className="hour-value">{project.remainingHours?.toFixed(1) || 0}h</span>
                          </div>
                          <div className="hour-bar-container">
                            <div 
                              className="hour-bar remaining"
                              style={{ width: `${getHoursBarWidth(project.remainingHours || 0, maxHours)}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Financial Overview with Visual Elements */}
              <div className="financial-section">
                <h5>
                  <DollarSign size={16} />
                  Financial Overview
                </h5>
                <div className="financial-cards">
                  <div className="financial-card budget">
                    <div className="financial-icon">
                      <DollarSign size={24} />
                    </div>
                    <div className="financial-info">
                      <span className="financial-label">Project Budget</span>
                      <span className="financial-value">
                        {formatCurrency(project.financials?.timeAndMaterialContract?.projectBudget || 0)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="financial-card actual">
                    <div className="financial-icon">
                      <TrendingUp size={24} />
                    </div>
                    <div className="financial-info">
                      <span className="financial-label">Actual Revenue</span>
                      <span className="financial-value">
                        {formatCurrency(project.financials?.metrics?.actualRevenue || 0)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="financial-card estimated">
                    <div className="financial-icon">
                      <BarChart3 size={24} />
                    </div>
                    <div className="financial-info">
                      <span className="financial-label">Estimated Revenue</span>
                      <span className="financial-value">
                        {formatCurrency(project.financials?.metrics?.estimatedRevenue || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'details':
        return (
          <div className="tab-content">
            {/* Project Info Grid with Icons */}
            <div className="project-info-grid">
              <div className="info-card">
                <div className="info-header">
                  <Calendar size={16} />
                  <span>Timeline</span>
                </div>
                <div className="info-content">
                  <div className="info-row">
                    <span className="info-label">Created</span>
                    <span className="info-value">{formatDate(project.createdAt)}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Last Updated</span>
                    <span className="info-value">{formatDate(project.updatedAt)}</span>
                  </div>
                </div>
              </div>

              <div className="info-card">
                <div className="info-header">
                  <Building size={16} />
                  <span>Project Details</span>
                </div>
                <div className="info-content">
                  <div className="info-row">
                    <span className="info-label">Customer</span>
                    <span className="info-value">{project.customer?.companyName || 'Not specified'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Contract Type</span>
                    <span className="info-value">{formatContractType(project.financials?.contractType)}</span>
                  </div>
                </div>
              </div>

              <div className="info-card">
                <div className="info-header">
                  <Users size={16} />
                  <span>Customer Engagement</span>
                </div>
                <div className="info-content">
                  <div className="info-row-icon">
                    <span className="info-label">Customers Invited</span>
                    <div className="info-icon-value">
                      {formatYesNoIcon(project.customersInvited)}
                      <span>{formatYesNo(project.customersInvited)}</span>
                    </div>
                  </div>
                  <div className="info-row-icon">
                    <span className="info-label">Customers Joined</span>
                    <div className="info-icon-value">
                      {formatYesNoIcon(project.customersJoined)}
                      <span>{formatYesNo(project.customersJoined)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Team Members Section with Roles */}
            {project.teamMembers?.members && project.teamMembers.members.length > 0 && (
              <div className="user-billable-projects">
                <h5>
                  <Users size={16} />
                  Team Members ({project.teamMembers.members.length})
                </h5>
                <div className="projects-table-container">
                  <table className="projects-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                      </tr>
                    </thead>
                    <tbody>
                      {project.teamMembers.members.map((member, index) => (
                        <tr key={index}>
                          <td className="project-name-cell">
                            {member.firstName} {member.lastName}
                          </td>
                          <td className="billable-hours-cell">{member.emailId}</td>
                          <td className="role-cell">
                            {(() => {
                              // Try to get role from different possible sources
                              const role = member.role?.roleName || 
                                          member.role?.name || 
                                          member.role?.label || 
                                          member.role?.value || 
                                          member.role || 
                                          'Team Member';
                              return role;
                            })()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Current Phases Section */}
            {project.currentPhases && project.currentPhases.length > 0 && (
              <div className="user-billable-projects">
                <h5>
                  <Target size={16} />
                  Current Phases ({project.currentPhases.length})
                </h5>
                <div className="projects-table-container">
                  <table className="projects-table">
                    <thead>
                      <tr>
                        <th>Phase Name</th>
                      </tr>
                    </thead>
                    <tbody>
                      {project.currentPhases.map((phase, index) => (
                        <tr key={index}>
                          <td className="project-name-cell">{phase.phaseName}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <Target size={20} />
            <h3>Project Details</h3>
          </div>
          <button onClick={onClose} className="modal-close">
            <X size={16} />
          </button>
        </div>

        <div className="modal-body">
          {/* Project Profile Section */}
          <div className="user-profile-section">
            <div className="user-profile">
              <div className="user-avatar">
                <Target size={48} />
              </div>
                             <div className="user-info">
                 <h4 className="user-name">
                   <a 
                     href={`https://projects.onesourcevirtual.com/projects/${project.projectId}/overview/`}
                     target="_blank"
                     rel="noopener noreferrer"
                     className="project-link"
                   >
                     {project.projectName}
                   </a>
                 </h4>
                 <p className="user-role">
                   {project.owner?.firstName} {project.owner?.lastName}
                 </p>
                 <span className={`status-badge ${project.status?.label?.toLowerCase().replace(/\s+/g, '-') || 'unknown'}`}>
                   {project.status?.label || project.status}
                 </span>
               </div>
            </div>
            
            {/* Project Progress Section - Moved to top */}
            <div className="project-progress-section">
              <h5>
                <Target size={16} />
                Project Progress
              </h5>
              <div className="progress-chart">
                <div className="progress-circle-container">
                  <div 
                    className="progress-circle"
                    style={{
                      background: `conic-gradient(${getProgressColor(project.progressPercentage || 0)} ${(project.progressPercentage || 0) * 3.6}deg, #e2e8f0 0deg)`
                    }}
                  >
                    <div className="progress-inner">
                      <span className="progress-percentage">{project.progressPercentage?.toFixed(0) || 0}%</span>
                      <span className="progress-label">Complete</span>
                    </div>
                  </div>
                </div>
                <div className="progress-details">
                  <div className="progress-stat">
                    <Calendar size={14} />
                    <span>{project.projectAgeInDays || 0} days old</span>
                  </div>
                  <div className="progress-stat">
                    <Clock size={14} />
                    <span>{project.percentageBudgetedHoursConsumed?.toFixed(1) || 0}% budget used</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="modal-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`modal-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  '--tab-color': tab.color,
                  '--tab-active-color': tab.color
                }}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailModal;
